/* eslint-disable no-case-declarations */

import {
    BetaManagedAgentsAgentMessageEvent,
    BetaManagedAgentsAgentMCPToolResultEvent,
    BetaManagedAgentsAgentMCPToolUseEvent,
    BetaManagedAgentsAgentToolUseEvent,
    BetaManagedAgentsTextBlock,
    BetaManagedAgentsUserMessageEvent,
} from '@anthropic-ai/sdk/resources/beta/sessions';
import { TextMessagePart, ThreadMessageLike, useExternalStoreRuntime } from '@assistant-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnthropicManagedAgentSession } from '../store/AnthropicManagedAgentSessionContext';
import { AgentStreamEvent } from '../types';
import { SESSION_ERROR_MESSAGE_ID } from '@/Components/ai-ui/thread';

type McpToolUseWithResult = BetaManagedAgentsAgentMCPToolUseEvent & {
    mcpResult?: BetaManagedAgentsAgentMCPToolResultEvent;
};

function useAnthropicManagedAgentRuntime() {
    const { sessionStatus, events, sendMessage, sendInterruptMessage } = useAnthropicManagedAgentSession();
    // 1. The communication with the server is event based which means there is a delay between the user send a message
    // and the updated session status is received from the server. To provide better UX we are using local state to track if the message is being sent.
    // 2. onNew resolves (POST returns) before the SSE delivers session.status_running, so isRunning briefly drops to false while the run is still in flight. With onCancel present, useExternalStoreRuntime keeps the composer text until isRunning properly transitions true → false. But since it sees false at the wrong moment, it never registers the clean end-of-run and leaves the text in place.
    const [isSending, setIsSending] = useState(false);
    const [pendingUserText, setPendingUserText] = useState<string | null>(null);

    // Any SSE status update means the server has the message — no longer need the local flag
    useEffect(() => {
        setIsSending(false);
    }, [sessionStatus]);

    // Clear optimistic user message once the real user.message with matching text arrives
    useEffect(() => {
        if (pendingUserText === null) return;
        const lastUserMsg = events.filter((e) => e.type === 'user.message').at(-1);
        const confirmedText = (lastUserMsg as any)?.content?.find((b: any) => b.type === 'text')?.text;
        if (confirmedText === pendingUserText) setPendingUserText(null);
    }, [events, pendingUserText]);

    const messages = useMemo(() => {
        // Build MCP tool result lookup keyed by the originating tool use id
        const mcpResults = new Map<string, BetaManagedAgentsAgentMCPToolResultEvent>();
        for (const e of events) {
            if (e.type === 'agent.mcp_tool_result') {
                const r = e as BetaManagedAgentsAgentMCPToolResultEvent;
                mcpResults.set(r.mcp_tool_use_id, r);
            }
        }

        const filtered: AgentStreamEvent[] = events
            .filter(
                (e) =>
                    e.type === 'user.message' ||
                    e.type === 'agent.message' ||
                    e.type === 'agent.tool_use' ||
                    e.type === 'agent.mcp_tool_use',
            )
            .map((e) => {
                if (e.type === 'agent.mcp_tool_use') {
                    const result = mcpResults.get((e as BetaManagedAgentsAgentMCPToolUseEvent).id);
                    return result ? ({ ...e, mcpResult: result } as AgentStreamEvent) : e;
                }
                return e;
            });

        // Remove the initial system prompt message since it doesn't add much value and may contain internal details about the search setup
        const firstUserMsgIdx = filtered.findIndex((e) => e.type === 'user.message');
        if (firstUserMsgIdx !== -1) {
            const firstUserMsg = filtered[firstUserMsgIdx] as BetaManagedAgentsUserMessageEvent;
            const firstText = firstUserMsg.content?.find((b) => b.type === 'text')?.text ?? '';
            if (firstText.startsWith('[INITIAL_SEARCH_MESSAGE]')) {
                filtered.splice(firstUserMsgIdx, 1);
            }
        }

        // Optimistically show the user message before SSE confirms it
        const lastUserMsg = filtered.filter((e) => e.type === 'user.message').at(-1);
        const lastConfirmedText = (lastUserMsg as any)?.content?.find((b: any) => b.type === 'text')?.text ?? null;
        if (pendingUserText !== null && lastConfirmedText !== pendingUserText) {
            filtered.push({
                type: 'user.message',
                id: '__optimistic_user__',
                processed_at: '',
                content: [{ type: 'text', text: pendingUserText }],
            } as AgentStreamEvent);
        }

        if (sessionStatus === 'error') {
            filtered.push({
                type: 'session.error',
                id: SESSION_ERROR_MESSAGE_ID,
                processed_at: '',
            } as AgentStreamEvent);
        }

        return filtered;
    }, [events, sessionStatus, isSending, pendingUserText]);

    const onNew = useCallback(
        async (msg: any) => {
            const text = msg.content[0]?.type === 'text' ? msg.content[0].text : '';
            setPendingUserText(text);
            setIsSending(true);
            await sendMessage(text);
        },
        [sendMessage],
    );

    const onCancel = async () => {
        await sendInterruptMessage();
    };

    const runtime = useExternalStoreRuntime({
        messages,
        isRunning: isSending || sessionStatus === 'running',
        convertMessage,
        onNew,
        onCancel,
    });
    return runtime;
}

export default useAnthropicManagedAgentRuntime;

function extractMcpResult(
    serverName: string,
    toolName: string,
    resultEvent: BetaManagedAgentsAgentMCPToolResultEvent,
): unknown {
    if (!resultEvent.content?.length) {
        return resultEvent.is_error ? 'Error' : undefined;
    }

    const text = resultEvent.content
        .filter((b): b is BetaManagedAgentsTextBlock => b.type === 'text')
        .map((b) => b.text)
        .join('\n');

    try {
        const parsed = JSON.parse(text);
        return parsed;
    } catch {
        return text || undefined;
    }
}

const convertMessage = (streamEvent: AgentStreamEvent): ThreadMessageLike => {
    switch (streamEvent.type) {
        case 'user.message':
            const userMessageEvent = streamEvent as BetaManagedAgentsUserMessageEvent;
            return {
                id: userMessageEvent.id,
                role: 'user' as const,
                content: userMessageEvent.content.map((b) => {
                    switch (b.type) {
                        case 'text':
                            return { type: 'text' as const, text: b.text };
                        default:
                            throw new Error(`Unsupported content block type}: ${(b as any).type}`);
                    }
                }),
            };
        case 'agent.message':
            const agentMessageEvent = streamEvent as BetaManagedAgentsAgentMessageEvent;
            return {
                id: agentMessageEvent.id,
                role: 'assistant' as const,
                content: agentMessageEvent.content.map(
                    (b) => ({ type: 'text' as const, text: b.text }) as TextMessagePart,
                ),
            };
        case 'agent.mcp_tool_use':
            const mcpToolUseEvent = streamEvent as McpToolUseWithResult;
            return {
                id: mcpToolUseEvent.id,
                role: 'assistant' as const,
                content: [
                    {
                        type: 'tool-call' as const,
                        toolCallId: mcpToolUseEvent.id,
                        toolName: `${mcpToolUseEvent.mcp_server_name}/${mcpToolUseEvent.name}`,
                        argsText: JSON.stringify(mcpToolUseEvent.input, null, 2),
                        result: mcpToolUseEvent.mcpResult
                            ? extractMcpResult(
                                  mcpToolUseEvent.mcp_server_name,
                                  mcpToolUseEvent.name,
                                  mcpToolUseEvent.mcpResult,
                              )
                            : undefined,
                        isError: mcpToolUseEvent.mcpResult?.is_error === true || undefined,
                    },
                ],
            };
        case 'session.error':
            return {
                id: streamEvent.id,
                role: 'assistant' as const,
                content: [{ type: 'text' as const, text: SESSION_ERROR_MESSAGE_ID }],
            };
        default:
            const toolUseEvent = streamEvent as BetaManagedAgentsAgentToolUseEvent;
            return {
                id: toolUseEvent.id,
                role: 'assistant' as const,
                content: [
                    {
                        type: 'tool-call' as const,
                        toolCallId: toolUseEvent.id,
                        toolName: toolUseEvent.name,
                        argsText: JSON.stringify(toolUseEvent.input),
                    },
                ],
            };
    }
};
